import {
  InterviewObservabilityContext,
  interviewPerformanceNow,
  trackInterviewDuration,
  trackInterviewMetric,
} from './interviewObservability';

export type SubmitFailureKind =
  | 'processing'
  | 'rate_limit'
  | 'uncertain'
  | 'invalid_attempt_reuse'
  | 'answer_already_submitted'
  | 'authorization'
  | 'non_retryable';

export type SubmitRecoveryOptions = {
  maxTransientAttempts: number;
  transientDelayMs: number;
  maxProcessingPolls: number;
  processingDelayMs: number;
  maxProcessingDelayMs?: number;
  maxRateLimitAttempts?: number;
  maxAutomaticRateLimitDelayMs?: number;
  wait?: (delayMs: number) => Promise<void>;
  observability?: InterviewObservabilityContext;
  onReconciliationStarted?: () => void;
};

export type SubmitRecoveryFailure = Error & {
  kind: SubmitFailureKind;
  retryable: boolean;
  exhausted: boolean;
  originalError: any;
  retryAfterMs?: number | null;
};

export const INTERVIEW_PROCESSING_POLL_DELAY_CAP_MS = 2000;
export const INTERVIEW_AUTOMATIC_RATE_LIMIT_DELAY_CAP_MS = 5000;

export type SubmitFailureUiDecision = {
  phase: 'review';
  retainAttempt: boolean;
  stayOnCurrentQuestion: true;
  consumeNoAnswerAttempt: false;
};

export const getSubmitErrorCode = (error: any) => String(
  error?.response?.data?.error_code || error?.response?.data?.error || ''
).trim().toUpperCase();

export const classifySubmitFailure = (error: any): {
  kind: SubmitFailureKind;
  retryable: boolean;
} => {
  const status = Number(error?.response?.status) || null;
  const code = String(error?.code || '').trim().toUpperCase();
  const errorCode = getSubmitErrorCode(error);

  if (status === 409 && errorCode === 'ATTEMPT_IN_PROGRESS') {
    return { kind: 'processing', retryable: true };
  }
  if (status === 409 && errorCode === 'INVALID_ATTEMPT_REUSE') {
    return { kind: 'invalid_attempt_reuse', retryable: false };
  }
  if (status === 409 && errorCode === 'ANSWER_ALREADY_SUBMITTED') {
    return { kind: 'answer_already_submitted', retryable: false };
  }
  if (status === 429) {
    return { kind: 'rate_limit', retryable: true };
  }
  if (status === 401 || status === 403 || status === 404) {
    return { kind: 'authorization', retryable: false };
  }
  if (
    error?.submitOutcomeUncertain === true ||
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    status === null ||
    status === 408 ||
    status === 425 ||
    status >= 500
  ) {
    return { kind: 'uncertain', retryable: true };
  }
  return { kind: 'non_retryable', retryable: false };
};

const createRecoveryFailure = (
  error: any,
  kind: SubmitFailureKind,
  retryable: boolean,
  exhausted: boolean,
  retryAfterMs: number | null = null
): SubmitRecoveryFailure => {
  const failure = new Error(error?.message || 'Submit jawaban belum dapat dipastikan.') as SubmitRecoveryFailure;
  failure.name = 'SubmitRecoveryFailure';
  failure.kind = kind;
  failure.retryable = retryable;
  failure.exhausted = exhausted;
  failure.originalError = error;
  failure.retryAfterMs = retryAfterMs;
  return failure;
};

const getRetryAfterMs = (error: any): number | null => {
  const retryAfterSeconds = Number(error?.response?.data?.retry_after_seconds);
  return Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
    ? Math.ceil(retryAfterSeconds * 1000)
    : null;
};

export const resolveProcessingPollDelayMs = (
  error: any,
  options: Pick<SubmitRecoveryOptions, 'processingDelayMs' | 'maxProcessingDelayMs'>
) => {
  const fallbackMs = Math.max(1, Number(options.processingDelayMs) || 1);
  const capMs = Math.max(
    1,
    Number(options.maxProcessingDelayMs) || INTERVIEW_PROCESSING_POLL_DELAY_CAP_MS
  );
  const serverDelayMs = getRetryAfterMs(error);
  return Math.min(serverDelayMs || fallbackMs, capMs);
};

export const resolveRateLimitRetryDelayMs = (error: any) => getRetryAfterMs(error);

export const createAmbiguousSubmitResponseError = () => {
  const error: any = new Error('SUBMIT_RESPONSE_AMBIGUOUS');
  error.submitOutcomeUncertain = true;
  return error;
};

export const hasConfirmedSubmitSuccess = (response: any) => Boolean(
  response?.data?.status === true && response?.data?.data
);

export const resolveSubmitFailureUiDecision = (error: any): SubmitFailureUiDecision => ({
  phase: 'review',
  retainAttempt: error?.name === 'SubmitRecoveryFailure' && error?.retryable === true,
  stayOnCurrentQuestion: true,
  consumeNoAnswerAttempt: false,
});

export const submitAnswerWithAttemptReconciliation = async <T,>(
  operation: () => Promise<T>,
  options: SubmitRecoveryOptions
): Promise<T> => {
  const wait = options.wait || ((delayMs: number) => new Promise<void>((resolve) => {
    window.setTimeout(resolve, delayMs);
  }));
  let transientAttempts = 0;
  let processingPolls = 0;
  let rateLimitAttempts = 0;
  let requestAttempt = 0;
  let reconciliationStartedAt: number | null = null;

  const startReconciliation = () => {
    if (reconciliationStartedAt !== null) return;
    reconciliationStartedAt = interviewPerformanceNow();
    options.onReconciliationStarted?.();
    trackInterviewMetric('answer_reconciliation_started', {
      ...options.observability,
      requestAttempt,
      outcome: 'started',
    });
  };

  while (true) {
    requestAttempt += 1;
    const httpAttemptStartedAt = interviewPerformanceNow();
    try {
      const response = await operation();
      if (!hasConfirmedSubmitSuccess(response)) {
        throw createAmbiguousSubmitResponseError();
      }
      const resultClass = (response as any)?.data?.data?.idempotent_replay
        ? 'idempotent_replay'
        : 'completed';
      trackInterviewDuration('answer_http_attempt_ms', httpAttemptStartedAt, {
        ...options.observability,
        requestAttempt,
        outcome: 'success',
        resultClass,
        httpStatus: Number((response as any)?.status) || null,
      });
      if (reconciliationStartedAt !== null) {
        trackInterviewDuration('answer_reconciliation_completed', reconciliationStartedAt, {
          ...options.observability,
          requestAttempt,
          outcome: 'completed',
          resultClass,
          httpStatus: Number((response as any)?.status) || null,
        });
      }
      return response;
    } catch (error: any) {
      const classification = classifySubmitFailure(error);
      const httpStatus = Number(error?.response?.status) || null;
      trackInterviewDuration('answer_http_attempt_ms', httpAttemptStartedAt, {
        ...options.observability,
        requestAttempt,
        outcome: 'error',
        resultClass: classification.kind,
        httpStatus,
      });

      if (classification.kind === 'processing' && processingPolls < options.maxProcessingPolls) {
        startReconciliation();
        processingPolls += 1;
        const delayMs = resolveProcessingPollDelayMs(error, options);
        trackInterviewDuration('answer_reconciliation_poll_ms', httpAttemptStartedAt, {
          ...options.observability,
          requestAttempt,
          retryCount: processingPolls,
          outcome: 'processing',
          resultClass: 'processing',
          httpStatus,
        });
        trackInterviewMetric('answer_reconciliation_wait_ms', {
          ...options.observability,
          requestAttempt,
          retryCount: processingPolls,
          outcome: 'processing_poll',
        }, delayMs);
        await wait(delayMs);
        continue;
      }

      if (classification.kind === 'uncertain' && transientAttempts < options.maxTransientAttempts) {
        startReconciliation();
        transientAttempts += 1;
        const delayMs = options.transientDelayMs * transientAttempts;
        trackInterviewMetric('answer_reconciliation_wait_ms', {
          ...options.observability,
          requestAttempt,
          retryCount: transientAttempts,
          outcome: 'transient_backoff',
        }, delayMs);
        await wait(delayMs);
        continue;
      }

      if (classification.kind === 'rate_limit') {
        const retryAfterMs = resolveRateLimitRetryDelayMs(error);
        const maxRateLimitAttempts = Math.max(0, Number(options.maxRateLimitAttempts) || 0);
        const maxAutomaticDelayMs = Math.max(
          0,
          Number(options.maxAutomaticRateLimitDelayMs) || INTERVIEW_AUTOMATIC_RATE_LIMIT_DELAY_CAP_MS
        );
        if (
          retryAfterMs !== null &&
          retryAfterMs <= maxAutomaticDelayMs &&
          rateLimitAttempts < maxRateLimitAttempts
        ) {
          startReconciliation();
          rateLimitAttempts += 1;
          trackInterviewMetric('answer_reconciliation_wait_ms', {
            ...options.observability,
            requestAttempt,
            retryCount: rateLimitAttempts,
            outcome: 'rate_limited',
          }, retryAfterMs);
          await wait(retryAfterMs);
          continue;
        }

        if (reconciliationStartedAt !== null) {
          trackInterviewDuration('answer_reconciliation_exhausted', reconciliationStartedAt, {
            ...options.observability,
            requestAttempt,
            outcome: 'rate_limited',
            resultClass: 'rate_limit',
            httpStatus,
          });
        }
        throw createRecoveryFailure(error, classification.kind, true, true, retryAfterMs);
      }

      const exhausted = classification.retryable;
      if (reconciliationStartedAt !== null) {
        trackInterviewDuration('answer_reconciliation_exhausted', reconciliationStartedAt, {
          ...options.observability,
          requestAttempt,
          outcome: 'exhausted',
          resultClass: classification.kind,
          httpStatus,
        });
      }
      throw createRecoveryFailure(error, classification.kind, classification.retryable, exhausted);
    }
  }
};

export type SubmitAttemptCoordinator = {
  execute: <T>(
    attemptId: string,
    operation: () => Promise<T>,
    onConfirmedSuccess: (result: T) => Promise<void> | void
  ) => Promise<T>;
  hasActive: (attemptId: string) => boolean;
  isCompleted: (attemptId: string) => boolean;
};

export const createSubmitAttemptCoordinator = (): SubmitAttemptCoordinator => {
  const active = new Map<string, Promise<any>>();
  const completed = new Map<string, any>();

  return {
    execute<T> (
      attemptId: string,
      operation: () => Promise<T>,
      onConfirmedSuccess: (result: T) => Promise<void> | void
    ): Promise<T> {
      if (completed.has(attemptId)) return Promise.resolve(completed.get(attemptId) as T);
      const existing = active.get(attemptId);
      if (existing) return existing as Promise<T>;

      const promise = (async () => {
        const result = await operation();
        if (!completed.has(attemptId)) {
          completed.set(attemptId, result);
          await onConfirmedSuccess(result);
        }
        return result;
      })().finally(() => {
        if (active.get(attemptId) === promise) active.delete(attemptId);
      });

      active.set(attemptId, promise);
      return promise;
    },
    hasActive: (attemptId: string) => active.has(attemptId),
    isCompleted: (attemptId: string) => completed.has(attemptId),
  };
};
